import { restoreErrorMessage } from "@/api/archived";
import { useHarnessCapabilities } from "@/api/harnessCapabilities";
import { useUpdateHarnessPreferences } from "@/api/harnessPreferences";
import {
  TokenMeter,
  UsageRing,
  useUsage,
} from "@/components/billing/TokenMeter";
import {
  HarnessRowLabel,
  ModelRowLabel,
} from "@/components/MessageInput/components/harnessRows";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  DS_MENU_CONTENT_CLASS,
  DS_MENU_ITEM_CLASS,
  DS_MENU_SEPARATOR_CLASS,
} from "@/components/ui/dsMenu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { harnessLabel, harnessSeatVisible } from "@/constants/harnesses";
import { ROUTES } from "@/constants/routes";
import { useOpenSettings } from "@/context/OpenSettingsContext";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useHarnessDefault } from "@/hooks/useHarnessDraft";
import { Flags } from "@/providers/LaunchDarklyProvider";
import { cn } from "@/utils";
import { formatInteger } from "@/utils/currency";
import { format } from "date-fns";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Check, ChevronRight } from "lucide-react";
import toast from "react-hot-toast";

/**
 * The footer's usage ring with the model menu behind it (DS decision 045). The menu sets the
 * model every NEW chat starts on — the user's harness preference, which every session-less
 * composer seeds its draft from; a pick in a composer's pill is that surface's alone. One row per
 * harness in catalog order; a harness with models is a nested menu of them, a pick there setting
 * both; the check sits on the resolved default. Then one usage row, the way to Usage.
 *
 * Shown on the composer's rule — the harness-runtime flag on and the catalog loaded. Otherwise
 * the ring is the plain token meter it was, a click opening Usage. Usage stays hidden where it
 * is today (the real iOS container, an account without billing data); the ring then renders
 * empty and the menu carries the model alone.
 */
export function ModelMenu() {
  const flags = useFlags<Flags>();
  const { data: capabilities } = useHarnessCapabilities();
  const harnessDefault = useHarnessDefault();
  const usage = useUsage();
  const lg = useBreakpoint("lg");
  const openSettings = useOpenSettings();
  const updatePreferences = useUpdateHarnessPreferences();

  if (
    !harnessSeatVisible(undefined, !!flags.harnessRuntime, capabilities) ||
    !capabilities ||
    !harnessDefault
  ) {
    return <TokenMeter />;
  }

  const capability = capabilities.find((c) => c.id === harnessDefault.harness);
  const modelLabel = capability?.models.find(
    (m) => m.id === harnessDefault.model,
  )?.label;
  const label = `Model: ${harnessLabel(harnessDefault.harness, capability?.label)}${modelLabel ? `, ${modelLabel}` : ""}`;
  const title = usage ? `Usage: ${usage.pct}% · ${label}` : label;

  const pick = (harness: string, model: string | undefined) => {
    if (harness === harnessDefault.harness && model === harnessDefault.model) {
      return;
    }
    updatePreferences.mutate(
      { default_harness: harness, default_model: model ?? null },
      {
        onError: (err) =>
          toast.error(restoreErrorMessage(err, "Failed to set the model")),
      },
    );
  };

  // Non-modal like the account quick-nav beside it: the usage row opens the
  // Manage modal, and a modal menu's body lock outlives it.
  return (
    <DropdownMenu modal={false}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={title}
              data-onboarding-target="footer-usage"
              // The held-open wash keys off aria-expanded: the tooltip trigger
              // writes data-state onto the same button and clobbers the menu's.
              className="inline-flex size-8 shrink-0 items-center justify-center rounded-md outline-none transition-colors hover:bg-n-05 aria-expanded:bg-n-10 focus-visible:outline-2 focus-visible:outline-rust focus-visible:outline-offset-[-2px]"
            >
              <UsageRing pct={usage?.pct ?? 0} />
            </button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="top" align="end">
          {title}
        </TooltipContent>
      </Tooltip>
      {/* On lg the menu flies out right of the pane edge, bottom-aligned, like
          the account quick-nav; below lg the rail is a drawer and it opens up. */}
      <DropdownMenuContent
        side={lg ? "right" : "top"}
        align="end"
        sideOffset={8}
        collisionPadding={8}
        className={cn("w-60", DS_MENU_CONTENT_CLASS)}
      >
        <DropdownMenuLabel className="px-2 py-1.5 text-xs font-medium text-n-50">
          Model
        </DropdownMenuLabel>
        {capabilities.map((option) => {
          const current = option.id === harnessDefault.harness;
          if (!option.models.length) {
            return (
              <DropdownMenuItem
                key={option.id}
                className={DS_MENU_ITEM_CLASS}
                disabled={!option.available}
                onSelect={() => pick(option.id, undefined)}
              >
                <HarnessRowLabel capability={option} />
                {current && <Check className="text-n-90" />}
              </DropdownMenuItem>
            );
          }
          return (
            <DropdownMenuSub key={option.id}>
              <DropdownMenuSubTrigger
                className={cn(DS_MENU_ITEM_CLASS, "data-[state=open]:bg-n-05")}
                disabled={!option.available}
              >
                <HarnessRowLabel capability={option} />
                {current && <Check className="text-n-90" />}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent
                className={cn("w-60", DS_MENU_CONTENT_CLASS)}
              >
                {option.models.map((model) => (
                  <DropdownMenuItem
                    key={model.id}
                    className={DS_MENU_ITEM_CLASS}
                    onSelect={() => pick(option.id, model.id)}
                  >
                    <ModelRowLabel model={model} />
                    {current && model.id === harnessDefault.model && (
                      <Check className="text-n-90" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          );
        })}
        {usage && (
          <>
            <DropdownMenuSeparator className={DS_MENU_SEPARATOR_CLASS} />
            <DropdownMenuItem
              className={DS_MENU_ITEM_CLASS}
              onSelect={() => openSettings(ROUTES.SETTINGS.USAGE)}
            >
              <UsageRing pct={usage.pct} size={14} stroke={2} />
              <span className="flex min-w-0 flex-1 flex-col py-1">
                <span className="truncate">
                  {usage.pct}% of {formatInteger(usage.total)} credits
                </span>
                {usage.resetsAt && (
                  <span className="text-xs text-n-50">
                    Resets {format(usage.resetsAt, "MMM d")}
                  </span>
                )}
              </span>
              <ChevronRight className="text-n-40" />
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
